'use client';

import { usePageAction } from '@cc-shopping/platform-analytics/hooks/usePageAction';
import { Box, type SxProps, Typography } from '@mui/material';
import type { ReactNode } from 'react';

type ReadMoreToggleProps = {
  children: ReactNode;
  readMoreStyles?: SxProps;
  containerStyles?: SxProps;
};

const defaultReadMoreStyles: SxProps = {
  fontWeight: 'bold',
  cursor: 'pointer',
  color: 'lime.tintLP2',
  alignItems: 'center',
  display: 'inline-flex',
  maxWidth: 'inherit',
  gap: 0.5,
  '&:hover': {
    textDecoration: 'underline',
    textDecorationColor: 'lime.tintLP2',
  },
};

export function ReadMoreToggle({
  children,
  readMoreStyles = defaultReadMoreStyles,
  containerStyles,
}: ReadMoreToggleProps) {
  const firePageAction = usePageAction({
    actionName: 'simplified_ru_read_more',
    actionSection: 'cc_roundup',
  });

  return (
    <Box
      component="details"
      sx={{
        display: 'inline-flex',
        verticalAlign: 'baseline',
        ...containerStyles,
        '&:not([open])': {
          '.read-more-toggle-show-less': {
            display: 'none',
          },
          '> summary': {
            marginLeft: '0.5rem',
          },
        },
        '&[open]': {
          flexDirection: 'column-reverse',
          '> summary': { marginTop: '2rem' },
          '.read-more-toggle-read-more': { display: 'none' },
          '.read-more-toggle-show-less': { display: 'inline-flex' },
        },
      }}
      onToggle={(e: React.SyntheticEvent) => {
        // Ignore nested <details> (e.g. accordion items) toggle events.
        if (e.target !== e.currentTarget) {
          return;
        }
        firePageAction({
          action_type: (e.currentTarget as HTMLDetailsElement).open ? 'expand' : 'collapse',
        });
      }}
    >
      <Typography
        component="summary"
        variant="textSmall"
        sx={{
          ...readMoreStyles,
          width: 'fit-content',
          listStyle: 'none',
          '&::-webkit-details-marker': { display: 'none' },
        }}
      >
        <span className="read-more-toggle-read-more">Read More</span>
        <span className="read-more-toggle-show-less">Show Less</span>
      </Typography>
      <Box
        sx={{
          marginTop: '1.5rem',
          // Temporary solution to allow accordions to render appropriately in the Read More Content
          // due to hero section background color.
          'details > summary:hover': {
            backgroundColor: 'transparent',
            outline: 'none',
          },
          'details .expand-icon': {
            color: 'white',
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
